import type { Directive, DirectiveBinding } from 'vue';

interface TruncateTextOptions {
  maxLength?: number;
  showMoreText?: string;
  showLessText?: string;
  buttonClass?: string;
  dynamicText?: string;
}

interface TruncateTextData {
  originalText: string;
  isExpanded: boolean;
  button?: HTMLButtonElement;
  listener?: () => void;
}

interface TruncateTextElement extends HTMLElement {
  _truncateTextData?: TruncateTextData;
}

const setupTruncate = (
  el: TruncateTextElement,
  binding: DirectiveBinding<TruncateTextOptions>,
  originalText: string,
  isExpanded = false
): void => {
  const maxLength = binding.value?.maxLength;
  const showMoreText = binding.value?.showMoreText || 'Show more';
  const showLessText = binding.value?.showLessText || 'Show less';
  const buttonClass = binding.value?.buttonClass || '';

  if (el._truncateTextData?.button && el._truncateTextData?.listener) {
    el._truncateTextData.button.removeEventListener('click', el._truncateTextData.listener);
    el._truncateTextData.button.remove();
  }

  if (!maxLength || originalText.length <= maxLength) {
    el.textContent = originalText;
    el._truncateTextData = { originalText, isExpanded: el._truncateTextData?.isExpanded || false };
    return;
  }

  const truncatedText = originalText.slice(0, maxLength) + '... ';
  let isTruncatedTextVisible = !isExpanded;

  const showMoreButton = document.createElement('button');
  showMoreButton.setAttribute('tabindex', '0');
  showMoreButton.setAttribute('class', buttonClass);

  const updateView = (): void => {
    if (isTruncatedTextVisible) {
      el.textContent = truncatedText;
      showMoreButton.textContent = showMoreText;
      showMoreButton.setAttribute('aria-expanded', 'false');
    } else {
      el.textContent = `${originalText} `;
      showMoreButton.textContent = showLessText;
      showMoreButton.setAttribute('aria-expanded', 'true');
    }
    el.appendChild(showMoreButton);
  };

  const listener = (): void => {
    isTruncatedTextVisible = !isTruncatedTextVisible;
    updateView();
    showMoreButton.focus();
    el._truncateTextData.isExpanded = !isTruncatedTextVisible;
  };

  showMoreButton.addEventListener('click', listener);

  el._truncateTextData = {
    originalText,
    button: showMoreButton,
    listener,
    isExpanded: !isTruncatedTextVisible,
  };

  updateView();
};

const truncateText = (
  el: TruncateTextElement,
  binding: DirectiveBinding<TruncateTextOptions>
): void => {
  const originalText = binding.value?.dynamicText || el.textContent || '';
  const prevData = el._truncateTextData;

  if (!prevData || prevData.originalText !== originalText)
    setupTruncate(el, binding, originalText, prevData?.isExpanded);
};

const vTruncateText: Directive<TruncateTextElement, TruncateTextOptions> = {
  mounted(el, binding) {
    truncateText(el, binding);
  },
  updated(el, binding) {
    truncateText(el, binding);
  },
  unmounted(el) {
    if (el._truncateTextData?.button && el._truncateTextData?.listener)
      el._truncateTextData.button.removeEventListener('click', el._truncateTextData.listener);
    delete el._truncateTextData;
  },
};

export default vTruncateText;
